import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Photo {
  id: number;
  album_id: number;
  url: string;
  caption: string | null;
  sort_order: number;
}

interface Album {
  id: number;
  category_id: number;
  title: string;
  description: string | null;
  cover_image: string | null;
  sort_order: number;
}

interface SortablePhotoItemProps {
  photo: Photo;
  isSelected: boolean;
  selectMode: boolean;
  onToggleSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onSetCover: (url: string) => void;
  albums: Album[];
  onMoveAlbum: (photoId: number, newAlbumId: number) => void;
}

function SortablePhotoItem({
  photo,
  isSelected,
  selectMode,
  onToggleSelect,
  onDelete,
  onSetCover,
  albums,
  onMoveAlbum,
}: SortablePhotoItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const [showMoveMenu, setShowMoveMenu] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto' as const,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative aspect-[3/4] rounded-xl overflow-hidden bg-warm-100 cursor-pointer ${
        isSelected ? 'ring-2 ring-dark ring-offset-2' : ''
      }`}
      onClick={() => {
        if (selectMode) onToggleSelect(photo.id);
      }}
    >
      <img
        src={photo.url}
        alt={photo.caption || 'Foto'}
        className="w-full h-full object-cover"
        draggable={false}
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300" />

      {/* Drag handle */}
      {!selectMode && (
        <button
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 z-20 w-7 h-7 flex items-center justify-center rounded bg-white/80 text-dark/60 hover:bg-white cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
          onClick={(e) => e.stopPropagation()}
          title="Arraste para reordenar"
        >
          <i className="ri-drag-move-2-line text-xs" />
        </button>
      )}

      {/* Checkbox de seleção */}
      {selectMode && (
        <div
          className="absolute top-2 left-2 z-20"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(photo.id);
          }}
        >
          <div className={`w-6 h-6 rounded-md flex items-center justify-center border-2 transition-colors ${
            isSelected ? 'bg-dark border-dark' : 'bg-white/80 border-white/60'
          }`}>
            {isSelected && <i className="ri-check-line text-white text-sm" />}
          </div>
        </div>
      )}

      {/* Botão excluir */}
      {!selectMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(photo.id);
          }}
          className="absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
        >
          <i className="ri-delete-bin-line text-sm" />
        </button>
      )}

      {/* Botão Definir capa */}
      {!selectMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSetCover(photo.url);
          }}
          className="absolute bottom-2 left-2 z-20 px-2 py-1 bg-white/90 text-dark text-[10px] font-medium rounded opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
        >
          <i className="ri-image-line mr-1" />
          Definir capa
        </button>
      )}

      {/* Botão Mover álbum */}
      {!selectMode && (
        <div className="absolute bottom-2 right-2 z-20">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMoveMenu((prev) => !prev);
            }}
            className="px-2 py-1 bg-white/90 text-dark text-[10px] font-medium rounded opacity-0 group-hover:opacity-100 transition-opacity shadow-sm flex items-center gap-1"
          >
            <i className="ri-folder-transfer-line" />
            Mover
          </button>

          {showMoveMenu && (
            <div
              className="absolute bottom-8 right-0 z-50 bg-white rounded-lg border border-warm-200 shadow-lg p-2 w-48"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-[10px] font-semibold text-dark/40 uppercase tracking-wider mb-1.5 px-1">
                Mover para álbum
              </p>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {albums
                  .filter((a) => a.id !== photo.album_id)
                  .map((album) => (
                    <button
                      key={album.id}
                      onClick={() => {
                        onMoveAlbum(photo.id, album.id);
                        setShowMoveMenu(false);
                      }}
                      className="w-full text-left px-2 py-1.5 rounded text-xs text-dark hover:bg-warm-100 transition-colors"
                    >
                      {album.title}
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface Props {
  photos: Photo[];
  selectMode: boolean;
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onSetCover: (url: string) => void;
  onReorder: (photos: Photo[]) => void;
  albums: Album[];
  onMoveAlbum: (photoId: number, newAlbumId: number) => void;
}

export default function SortablePhotoGrid({
  photos,
  selectMode,
  selectedIds,
  onToggleSelect,
  onDelete,
  onSetCover,
  onReorder,
  albums,
  onMoveAlbum,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = photos.findIndex((p) => p.id === active.id);
    const newIndex = photos.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(photos, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={photos.map((p) => p.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {photos.map((photo) => (
            <SortablePhotoItem
              key={photo.id}
              photo={photo}
              isSelected={selectedIds.has(photo.id)}
              selectMode={selectMode}
              onToggleSelect={onToggleSelect}
              onDelete={onDelete}
              onSetCover={onSetCover}
              albums={albums}
              onMoveAlbum={onMoveAlbum}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}